/**
 * CN-0008: Новая структура прав пользователя
 * 
 * Права разделены на:
 * 1. global - глобальные права на уровне всей системы
 * 2. farms - права на уровне конкретных ферм
 */

export interface EventQueuePermissions {
  canViewMyQueue: boolean;      // Может видеть свои назначенные задачи
  canViewAllQueue: boolean;      // Может видеть общую очередь (unassigned)
  canAssignEvents: boolean;      // Может назначать задачи на других
  canCreateEvents: boolean;      // Может создавать новые задачи
}

export interface FarmActions {
  farms?: string[];
  fields?: string[];
  plots?: string[];
  beds?: string[];
  crops?: string[];
  plantings?: string[];
  events?: string[];
  templates?: string[];
  analytics?: string[];
  attributes?: string[];
}

export interface FarmPermissions {
  actions: FarmActions;
  eventQueue: EventQueuePermissions;
}

export interface UserActions {
  global: FarmActions;
  farms: {
    [farmId: string]: FarmPermissions;
  };
}

export interface User {
  id: string;
  telegramId?: number;
  role: string;
  actions: UserActions;
  username?: string;
  pseudo?: string;
}

/**
 * Проверяет наличие глобального права
 */
export function hasGlobalAction(user: User | null, category: keyof FarmActions, action: string): boolean {
  if (!user?.actions?.global) return false;
  const categoryActions = user.actions.global[category];
  return Array.isArray(categoryActions) && categoryActions.includes(action);
}

/**
 * Проверяет наличие права на уровне фермы
 */
export function hasFarmAction(
  user: User | null,
  farmId: string | number,
  category: keyof FarmActions,
  action: string
): boolean {
  if (!user?.actions?.farms) return false;
  const farmPerms = user.actions.farms[String(farmId)];
  if (!farmPerms?.actions) return false;
  
  const categoryActions = farmPerms.actions[category];
  return Array.isArray(categoryActions) && categoryActions.includes(action);
}

/**
 * Проверяет наличие права глобально ИЛИ на уровне фермы
 */
export function hasAction(
  user: User | null,
  category: keyof FarmActions,
  action: string,
  farmId?: string | number
): boolean {
  // Сначала проверяем глобальные права
  if (hasGlobalAction(user, category, action)) {
    return true;
  }
  
  // Потом проверяем права на уровне фермы
  if (farmId !== undefined) {
    return hasFarmAction(user, farmId, category, action);
  }
  
  return false;
}

/**
 * Получает права на работу с очередью событий для фермы
 */
export function getEventQueuePermissions(
  user: User | null,
  farmId: string | number
): EventQueuePermissions {
  const defaultPerms: EventQueuePermissions = {
    canViewMyQueue: false,
    canViewAllQueue: false,
    canAssignEvents: false,
    canCreateEvents: false,
  };
  
  if (!user?.actions?.farms) return defaultPerms;
  
  const farmPerms = user.actions.farms[String(farmId)];
  return farmPerms?.eventQueue || defaultPerms;
}

/**
 * Проверяет, является ли пользователь суперадмином
 */
export function isSuperAdmin(user: User | null): boolean {
  if (!user?.role) return false;
  return ['superadmin', 'superuser', 'admin'].includes(String(user.role).toLowerCase());
}

/**
 * Проверяет права на управление атрибутами культур
 * (для обратной совместимости со старым кодом)
 */
export function canManageAttributes(
  user: User | null,
  farmId?: string | number
): boolean {
  if (isSuperAdmin(user)) return true;
  
  // Проверяем новые права
  if (hasAction(user, 'crops', 'crops.update', farmId) ||
      hasAction(user, 'crops', 'crops.manage', farmId) ||
      hasAction(user, 'attributes', 'attributes.manage', farmId) ||
      hasAction(user, 'attributes', 'attributes.update', farmId)) {
    return true;
  }
  
  return false;
}

/**
 * Проверяет права на управление пользователями
 */
export function canManageUsers(user: User | null): boolean {
  if (isSuperAdmin(user)) return true;
  return hasGlobalAction(user, 'farms', 'users.update') || 
         hasGlobalAction(user, 'farms', 'users.manage');
}
